import {useTranslation} from 'react-i18next';
import {ChevronRight, Plus} from 'lucide-react';
import {Card, Skeleton, cn} from '@chekinapp/ui';
import type {UpsellItem} from '../useUpsellItems';

const MAX_HOST_OFFERS = 3;

const ROW_DIVIDER =
  'relative after:absolute after:left-[56px] after:right-0 after:top-0 after:h-px after:bg-[var(--chekin-divider-subtle)] after:content-[""] first:after:hidden';

function OfferThumb({item}: {item: UpsellItem}) {
  if (!item.imageUrl) {
    return (
      <span className="size-11 shrink-0 rounded-[12px] [background:var(--chekin-gradient-brand)]" />
    );
  }
  return (
    <img
      src={item.imageUrl}
      alt=""
      loading="lazy"
      className="size-11 shrink-0 rounded-[12px] object-cover"
    />
  );
}

function HostOfferRow({item}: {item: UpsellItem}) {
  const {t} = useTranslation();

  return (
    <li className={cn('flex items-center gap-2', ROW_DIVIDER)}>
      <button
        type="button"
        onClick={item.onSelect}
        className="group flex min-w-0 flex-1 items-center gap-3 rounded-[12px] py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--chekin-color-brand-blue)]"
      >
        <OfferThumb item={item} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-semibold leading-5 text-[var(--chekin-color-brand-navy)] transition-colors group-hover:text-[var(--chekin-color-brand-blue)]">
            {item.title}
          </span>
          {item.description && (
            <span className="block truncate text-[12px] leading-[17px] text-[var(--chekin-text-secondary)]">
              {item.description}
            </span>
          )}
        </span>
      </button>
      {item.price && (
        <span className="shrink-0 text-right text-[15px] font-bold tabular-nums text-[var(--chekin-color-brand-navy)]">
          {item.showFromPrefix && (
            <span className="block text-[12px] font-medium leading-3 text-[var(--chekin-text-secondary)]">
              {t('from')}
            </span>
          )}
          {item.price}
        </span>
      )}
      {item.onAdd && (
        <button
          type="button"
          onClick={item.onAdd}
          aria-label={`${item.ctaLabel} — ${item.title}`}
          className="grid size-8 shrink-0 place-items-center rounded-full bg-[var(--chekin-blue-tint-info)] text-[var(--chekin-color-brand-blue)] transition-colors hover:bg-[var(--chekin-color-brand-blue)] hover:text-[var(--chekin-color-white)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--chekin-color-brand-blue)] focus-visible:ring-offset-2"
        >
          <Plus size={16} strokeWidth={2.25} />
        </button>
      )}
    </li>
  );
}

type Props = {
  items: UpsellItem[];
  onSeeAll: () => void;
};

export function HostOffersList({items, onSeeAll}: Props) {
  const {t} = useTranslation();
  const visible = items.slice(0, MAX_HOST_OFFERS);

  return (
    <Card className="px-[14px] py-0.5" data-testid="home-host-offers">
      <ul className="m-0 list-none p-0">
        {visible.map(item => (
          <HostOfferRow key={item.id} item={item} />
        ))}
      </ul>
      {items.length > MAX_HOST_OFFERS && (
        <button
          type="button"
          onClick={onSeeAll}
          className="relative flex h-[46px] w-full items-center justify-center gap-1 text-[14px] font-semibold text-[var(--chekin-color-brand-blue)] after:absolute after:inset-x-0 after:top-0 after:h-px after:bg-[var(--chekin-divider-subtle)] after:content-[''] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--chekin-color-brand-blue)]"
        >
          {t('see_all_offers_n', {n: items.length})}
          <ChevronRight size={16} />
        </button>
      )}
    </Card>
  );
}

export function HostOffersListSkeleton() {
  return (
    <Card
      className="px-[14px] py-0.5"
      aria-hidden
      data-testid="home-host-offers-skeleton"
    >
      {[0, 1, 2].map(i => (
        <div key={i} className="flex items-center gap-3 py-3">
          <Skeleton className="size-11 rounded-[12px]" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="size-8 rounded-full" />
        </div>
      ))}
    </Card>
  );
}
